import { LayoutSizeContext } from 'App';
import DropdownSelector from 'components/DropdownSelector';
import { ErrorMessage } from 'components/Widgets/ErrorMessage';
import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import ConditionIcons from 'features/Weather/components/ConditionIcons/ConditionIcons';
import { useContext, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useGetDetailedForecastQuery } from 'services/weather/weather.api';
import { selectUserSelectedLocation } from 'store/selectors/User/userSelectors';
import WeatherWidgetDetails from '../../../../components/WeatherWidgetDetails/WeatherWidgetDetails';
import WeatherWidgetSelector from '../../../../components/WeatherWidgetSelector/WeatherWidgetSelector';
import { weatherDetails } from '../../config/forecast.config';
import { timeOfDayOrder } from '../../features/DetailedForecast/config/detailedForecast.config';
import { layoutConfig, route } from './config/DetailedForecastWidget.config';
import {
  getDetailedForecastDays,
  getDetailedForecastTileAriaLabel,
  getDetailedForecastTimeOfDayItems,
  getDetailedForecastWidgetLayout,
} from './utils/DetailedForecastWidget.utils';

const DetailedForecastWidget = ({ layout, dimensions, containerSizePx }) => {
  const layoutSize = useContext(LayoutSizeContext);
  const userSelectedLocation = useSelector(selectUserSelectedLocation);
  const {
    data: detailedForecast = [],
    isLoading: loadingDetailedForecast,
    isError: isErrorDetailedForecast,
    error: detailedForecastError,
    refetch: refetchDetailedForecast,
    fulfilledTimeStamp,
  } = useGetDetailedForecastQuery(userSelectedLocation);
  const forecastDays = useMemo(() => getDetailedForecastDays(detailedForecast), [detailedForecast]);
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedTimeIndex, setSelectedTimeIndex] = useState(0);
  const activeDay = selectedDay || forecastDays[0];
  const forecastTimeOfDayItems = useMemo(
    () => getDetailedForecastTimeOfDayItems(detailedForecast, activeDay, timeOfDayOrder),
    [detailedForecast, activeDay],
  );
  const selectedTimeItem = forecastTimeOfDayItems[selectedTimeIndex] || forecastTimeOfDayItems[0];
  const {
    arrowButtonWidth,
    arrowIconSize,
    compact1x1VisibleCount,
    compact1x2ColumnCount,
    rowTileIconSize,
    tileIconSize,
    tileTextStyle,
  } = getDetailedForecastWidgetLayout({ containerSizePx, layoutSize, dimensions });
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const visibleNumOfItems = config.visibleCount({
    compact1x1VisibleCount,
    compact1x2ColumnCount,
  });

  if (isErrorDetailedForecast && !loadingDetailedForecast) {
    return (
      <ErrorMessage
        message={detailedForecastError?.message ?? 'Unable to load detailed forecast.'}
        retryRoute={route}
        onRetry={() => refetchDetailedForecast()}
      />
    );
  }

  if (loadingDetailedForecast)
    return <WidgetLoadingAnimation widgetName={'DetailedForecast'} dimensions={dimensions} />;

  if (detailedForecast && detailedForecast.length === 0) {
    return (
      <section aria-label="Detailed forecast widget">
        <WidgetTimeUpdate
          route={route}
          updateIntervalMinutes={60}
          updatedAt={fulfilledTimeStamp ?? null}
        />
        <p className="p-3 mb-0" role="status">
          No data found.
        </p>
      </section>
    );
  }

  return (
    <section aria-label="Detailed forecast widget" className={config.wrapperClassName}>
      <WidgetTimeUpdate
        route={route}
        compact={config.compactTimeUpdate}
        updatedAt={fulfilledTimeStamp ?? null}
      />
      <div className={config.contentClassName}>
        <DropdownSelector
          options={forecastDays}
          selectedOption={forecastDays.indexOf(activeDay)}
          setSelected={(index) => {
            setSelectedDay(forecastDays[index]);
            setSelectedTimeIndex(0);
          }}
          triggerStyle={{ width: '100%' }}
        />
        <div
          className="pt-2"
          style={{
            flex: `0 0 ${config.selectorHeight}`,
            height: config.selectorHeight,
            minHeight: 0,
          }}
        >
          <WeatherWidgetSelector
            items={forecastTimeOfDayItems}
            selectedIndex={selectedTimeIndex}
            visibleNumOfItems={visibleNumOfItems}
            selectorMode={config.selectorMode}
            gridColumnCount={compact1x2ColumnCount}
            gap={config.selectorGap}
            arrowButtonWidth={arrowButtonWidth}
            arrowIconSize={arrowIconSize}
            listContainerStyle={config.selectorListStyle}
            tileStyle={config.selectorTileStyle}
            ariaLabel="Detailed forecast times of day"
            previousButtonLabel="Show previous detailed forecast time"
            nextButtonLabel="Show next detailed forecast time"
            onSelect={(index) => setSelectedTimeIndex(index)}
            getKey={(item) => item?.iso8601}
            getTileProps={(item) => ({
              label: item?.day_segment,
              value: `${item?.temperature ?? '--'}°F`,
              ariaLabel: getDetailedForecastTileAriaLabel(item),
              icon: (
                <span
                  aria-hidden="true"
                  className="d-flex h-100 w-100 align-items-center justify-content-center"
                >
                  <ConditionIcons conditionId={parseInt(item?.icon || '0', 10)} />
                </span>
              ),
              iconStyle: {
                width: config.selectorMode === 'row' ? rowTileIconSize : tileIconSize,
                height: config.selectorMode === 'row' ? rowTileIconSize : tileIconSize,
              },
              labelStyle: tileTextStyle,
              valueStyle: tileTextStyle,
            })}
          />
        </div>
        <WeatherWidgetDetails
          selectedItem={selectedTimeItem}
          details={weatherDetails}
          ariaLabel={`Detailed forecast details for ${activeDay} ${
            selectedTimeItem?.day_segment ?? ''
          }`}
          layoutGroups={config.detailGroups}
          containerClassName={config.detailsClassName}
          containerStyle={config.detailsStyle({ layoutSize })}
          iconSize={config.detailsIconSize}
        />
      </div>
    </section>
  );
};

export default DetailedForecastWidget;
